import React, { useCallback, forwardRef, useRef, useImperativeHandle } from "react";
import { Box, IconButton } from "@mui/material";
import { StriimTypography, StriimIconButton } from "@striim/striim-ui-v2";
import AppSettingsWrapper from "./app-settings-wrapper";
import CloseIcon from "@mui/icons-material/Close";
import { useAppDispatch, useAppSelector } from "src/stores/redux/hooks";
import { getIsAppSettingsSidebarOpen, getAppSettingsDeployment } from "../../helpers/selectors";
import { setShowAppSettingsSidebarAction } from "src/stores/redux/features/flowdesigner/flowdesigner.slice";

interface AppSettingsSidebarProps {
    app: any;
    appStatus?: string;
}

interface AppSettingsSidebarRef {
    isDirty: () => boolean;
}

const AppSettingsSidebar = forwardRef<AppSettingsSidebarRef, AppSettingsSidebarProps>(({ app, appStatus }, ref) => {
    const isAppSettingsSidebarOpen = useAppSelector(getIsAppSettingsSidebarOpen);
    const appSettingsDeployment = useAppSelector(getAppSettingsDeployment);
    const appSettingsWrapperRef = useRef<any>(null);

    const dispatch = useAppDispatch();
    const handleCloseAppSettings = useCallback(() => {
        dispatch(setShowAppSettingsSidebarAction(false));
    }, [dispatch]);

    // Expose isDirty method to parent component
    useImperativeHandle(ref, () => ({
        isDirty: () => {
            return (
                isAppSettingsSidebarOpen &&
                appSettingsWrapperRef.current &&
                typeof appSettingsWrapperRef.current.isDirty === "function" &&
                appSettingsWrapperRef.current.isDirty()
            );
        }
    }));

    const title = appSettingsDeployment ? "Deployment Settings" : "App Settings";

    if (!isAppSettingsSidebarOpen || !app) return;
    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                backgroundColor: "white",
                borderLeft: "1px solid #e0e0e0",
                display: "flex",
                flexDirection: "column",
                position: "relative"
            }}
        >
            {/* Header */}
            <Box sx={{ borderBottom: "1px solid #e0e0e0", p: 2, flexShrink: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <StriimTypography variant="h2">{title}</StriimTypography>
                    {handleCloseAppSettings && (
                        <IconButton onClick={handleCloseAppSettings} size="small">
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            </Box>
            <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
                <AppSettingsWrapper
                    ref={appSettingsWrapperRef}
                    app={app}
                    appStatus={appStatus}
                    deployment={appSettingsDeployment}
                    onClose={handleCloseAppSettings}
                />
            </Box>
        </Box>
    );
});

export default AppSettingsSidebar;
